import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { EnvironmentInfo } from '@/lib/auth';

// Tipos
export interface User {
  id: string;
  email: string;
  nombre: string;
  apellido?: string;
  tipo: 'INTERNO' | 'CLIENTE';
  avatar_url?: string;
  cliente_id?: string;
  roles: string[];
}

// Re-export EnvironmentInfo as ActiveEnv for backwards compat
export type { EnvironmentInfo as ActiveEnv } from '@/lib/auth';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  activeEnv: EnvironmentInfo | null;

  // Acciones
  login: (user: User, accessToken: string) => void;
  logout: () => void;
  setUser: (user: User) => void;
  setAccessToken: (accessToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  setActiveEnv: (env: EnvironmentInfo | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      activeEnv: null,

      login: (user, accessToken) => {
        set({
          user,
          accessToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
          activeEnv: null,
        });
      },

      setUser: (user) => {
        set({ user, isAuthenticated: user !== null });
      },

      setAccessToken: (accessToken) => {
        set({ accessToken });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      setActiveEnv: (env) => {
        set({ activeEnv: env });
      },
    }),
    {
      name: 'qops-auth-storage',
      // Persist only user for page reload resilience.
      // accessToken stays in memory — API interceptor refreshes via httpOnly cookie on 401.
      // isAuthenticated is derived from user !== null (no ghost auth state).
      partialize: (state) => ({
        user: state.user,
        activeEnv: state.activeEnv,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isAuthenticated = state.user !== null;
        }
      },
    }
  )
);

// Hook para verificar permisos
export const useHasPermission = (_permission: string): boolean => {
  const { user } = useAuthStore();

  if (!user) return false;

  // Admin tiene todos los permisos
  if (user.roles.includes('ADMIN')) return true;

  // Verificar permiso específico
  // TODO: Implementar lógica de permisos basada en roles
  return true;
};

// Hook para verificar si es usuario interno
export const useIsInternal = (): boolean => {
  const { user } = useAuthStore();
  return user?.tipo === 'INTERNO';
};
